import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetAuthApi } from '@/hooks/UseApiCalls';
import type { FundLookupDtoIn, NedarimDecodeEntryDtoIn } from '@/models/in/NedarimDecodeModels';
import Button from '@/components/ui/Button';
import EditOrCreateDecodeRuleDialog from './EditOrCreateDecodeRuleDialog';
import DeleteDecodeRuleDialog from './DeleteDecodeRuleDialog';
import { DECODE_ENTRIES_URL } from './nedarimTransactionsFundAllocation';
import styles from './fundAllocationTabs.module.scss';

const DecodeRulesTab = ({ funds }: { funds: FundLookupDtoIn[] }) => {
  const { t } = useTranslation();
  const { data, isLoading, isError, refetch } = useGetAuthApi<NedarimDecodeEntryDtoIn[]>(DECODE_ENTRIES_URL);

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [ruleToEdit, setRuleToEdit] = useState<NedarimDecodeEntryDtoIn | null>(null);
  const [ruleToDelete, setRuleToDelete] = useState<NedarimDecodeEntryDtoIn | null>(null);

  const openCreate = () => {
    setRuleToEdit(null);
    setIsEditOpen(true);
  };
  const openEdit = (rule: NedarimDecodeEntryDtoIn) => {
    setRuleToEdit(rule);
    setIsEditOpen(true);
  };

  if (isLoading) return <div className={styles.loading}>{t('loading')}</div>;

  // A failed request must not read as "nothing here yet": an empty rules table looked exactly like a
  // working screen with no rules while the server was in fact answering with an error.
  if (isError) return <div className={styles.loadError}>{t('nedarimTransactionsFundAllocation.tabs.loadError')}</div>;

  const rows = data ?? [];

  return (
    <div className={styles.tabPanel}>
      <p className={`body-md-regular ${styles.intro}`}>{t('nedarimTransactionsFundAllocation.tabs.rulesIntro')}</p>

      <div className={styles.toolbar}>
        <span className={`body-sm-regular ${styles.count}`}>{t('nedarimTransactionsFundAllocation.tabs.rulesCount', { count: rows.length })}</span>
        <Button variant="primary" onClick={openCreate}>
          {t('nedarimTransactionsFundAllocation.tabs.addRule')}
        </Button>
      </div>

      <div className={styles.tableCard}>
        <div className={styles.scroll}>
          <table>
            <thead>
              <tr>
                <th>{t('nedarimTransactionsFundAllocation.tabs.rulesSourceLabel')}</th>
                <th>{t('nedarimTransactionsFundAllocation.tabs.assignedFund')}</th>
                <th>{t('nedarimTransactionsFundAllocation.tabs.rulesUpdatedBy')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={4} className={styles.muted} style={{ textAlign: 'center', padding: '32px' }}>
                    {t('nedarimTransactionsFundAllocation.tabs.rulesEmpty')}
                  </td>
                </tr>
              ) : (
                rows.map((rule) => (
                  <tr key={rule.id}>
                    <td className="body-sm-regular">
                      {rule.sourceValue}
                      {/* A campaign is the same row, kept from the fund card (HAT-1121). Saying so
                          here is what stops a delete from quietly removing a fund's campaign. */}
                      {rule.isCampaign && (
                        <span className={styles.campaignBadge} title={rule.campaignName ?? undefined}>
                          {t('nedarimTransactionsFundAllocation.tabs.rulesCampaignBadge')}
                        </span>
                      )}
                    </td>
                    <td className={`body-sm-regular ${styles.fundCell}`}>
                      {rule.fundId} {rule.fundName ? `— ${rule.fundName}` : ''}
                    </td>
                    <td className={`body-sm-regular ${styles.muted}`}>{rule.createdByEmail || '—'}</td>
                    <td>
                      <div className={styles.actionsCell}>
                        <Button variant="outline" onClick={() => openEdit(rule)}>
                          {t('nedarimTransactionsFundAllocation.tabs.edit')}
                        </Button>
                        <Button variant="outline" className="!text-red-500 !border-red-300" onClick={() => setRuleToDelete(rule)}>
                          {t('nedarimTransactionsFundAllocation.tabs.delete')}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {isEditOpen && (
        <EditOrCreateDecodeRuleDialog
          open={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          onSuccess={refetch}
          funds={funds}
          ruleToEdit={ruleToEdit}
        />
      )}
      {ruleToDelete && (
        <DeleteDecodeRuleDialog open={!!ruleToDelete} onClose={() => setRuleToDelete(null)} onSuccess={refetch} rule={ruleToDelete} />
      )}
    </div>
  );
};

export default DecodeRulesTab;
